import React, { useEffect, useState, createContext } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { ModalFooter, Scrollbar } from "../../../../Constants/StyledComponents";
import { useLayoutService } from "../../../../Hooks/useLayoutService";
import { editedLayoutGroupAtom, madeChangesToEditedLayoutGroupAtom, uneditedLayoutGroupAtom } from "../../../../Pages/Data/Visualize/DataReview/Atoms/Layout";
import { useModalProvider } from "../../../../Providers/ModalProvider";
import { modalityGraphGroupConfigAtom } from '../../../../Pages/Data/Visualize/DataReview/Atoms/ModalityGraphGroup';
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergFontSize, MobergTheme, MobergHeader, MobergShortTextInput, MobergDropdown, MobergColumn, MobergRow, MobergText, getColors, MobergNumberInput, MobergInputLabel, MobergIconSize } from '../../../../Moberg';
import ConfirmationModal from '../../../../Components/ConfirmationModal/ConfirmationModal';
import { RenderStrategy } from '../../../../Pages/Data/Visualize/DataReview/Types/Trace';
import { LineGraphModalities } from "./LineGraphModalities/LineGraphModalities"
import { HeatmapGraphModalities } from "./HeatmapGraphModalities/HeatmapGraphModalities"
import { SpectrogramGraphModalities } from "./SpectrogramGraphModalities/SpectrogramGraphModalities"
import { DEFAULT_DSA_DISPLAY_SCALE, DSA_FREQUENCY_LABELS } from '../../../../Pages/Data/Visualize/DataReview/Types/Trace'
import { ColorSpectrum } from '../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum';
import { ConfigureWindowModalHeader } from "./ConfigureWindowModalHeader"
import { isGenericAnalytic } from '../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails';
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { MultiGraphNumericDisplayConfiguration } from "./MultiGraphNumericDisplayConfiguration"
import { SortableGraphs } from "./SortableGraphs"
import { applyModalitySpecificDefaults } from '../../../../Constants/LabelSettings';
import { useTraceOptions } from '../../../../Pages/Data/Visualize/DataReview/Components/Modals/EditAnalysis/useTraceOptions';
import { useDataReviewPermissions } from '../../../../Hooks/useDataReviewPermissions';
import { permissionTooltip } from '../../../../Utils/permissionUtils';

export const ConfigureWindowModalContext = createContext({})

export function ConfigureWindowModal({ layoutId, windowId }) {
    const { createModal, close } = useModalProvider()
    const { modifyLayoutGroup } = useLayoutService()
    const setUneditedLayoutGroup = useSetRecoilState(uneditedLayoutGroupAtom)
    const setTimeSeriesGraphGroup = useSetRecoilState(modalityGraphGroupConfigAtom({ windowId, layoutId }))

    const madeChanges = useRecoilValue(madeChangesToEditedLayoutGroupAtom)

    const [editedLayoutGroup, setEditedLayoutGroup] = useRecoilState(editedLayoutGroupAtom)
    const [selectedGraphId, setSelectedGraphId] = useState()
    const [waitingForNetwork, setWaitingForNetwork] = useState(false)
    const [error, setError] = useState() // network error. useAsyncTask handles this better than defining the state here.
    const [validationErrors, setValidationErrors] = useState(new Map())
    const validationErrorsArray = Array.from(validationErrors.values())
    const red = getColors(MobergTheme.RED).main
    const { canManageDataReview } = useDataReviewPermissions()



    const currentLayout = editedLayoutGroup?.layouts.find(layout => layout.id === layoutId)
    const currentWindow = currentLayout?.areas.find(({ area }) => area === windowId)
    const currentGraph = !selectedGraphId && (currentWindow?.props?.graphs ?? []).length > 0
        ? currentWindow.props.graphs[0]
        : currentWindow?.props.graphs.find(graph => selectedGraphId && graph.id === selectedGraphId)

    const traceOptions = useTraceOptions({ includeAnalytics: true })

    useEffect(() => {
        if (!selectedGraphId && currentWindow?.props.graphs.length > 0) {
            setSelectedGraphId(currentWindow.props.graphs[0].id)
        }

    }, [currentWindow.props.graphs, selectedGraphId])

    function addGraph() {
        const newGraph = {
            id: `graph-${new Date(Date.now()).toISOString()}`,
            name: 'New graph',
            traces: [],
            renderStrategy: RenderStrategy.LINE,
            min: 0,
            max: 100,
            defaultMin: 0,
            defaultMax: 100,
            autoScale: true,
            summaryValueColumns: 1
        }

        // This is so complex, it probably should be being done by a reducer.
        setEditedLayoutGroup(previous => {
            return {
                ...previous,
                layouts: previous.layouts.map(layout => {
                    if (layout.id === layoutId) {
                        return {
                            ...layout,
                            areas: layout.areas.map(area => {
                                if (area.area === windowId) {
                                    return {
                                        ...area,
                                        props: {
                                            ...area.props,
                                            graphs: [...area.props.graphs ?? [], newGraph]
                                        }
                                    }
                                }
                                return area
                            })
                        }
                    }
                    return layout
                })
            }
        })

        setSelectedGraphId(newGraph.id)
    }

    function removeGraph(graphId) {
        // This is so complex, it probably should be being done by a reducer.
        setEditedLayoutGroup(previous => {
            return {
                ...previous,
                layouts: previous.layouts.map(layout => {
                    if (layout.id === layoutId) {
                        return {
                            ...layout,
                            areas: layout.areas.map(area => {
                                if (area.area === windowId) {
                                    return {
                                        ...area,
                                        props: {
                                            ...area.props,
                                            graphs: area.props.graphs?.filter(g => g.id !== graphId) ?? []
                                        }
                                    }
                                }
                                return area
                            })
                        }
                    }
                    return layout
                })
            }
        })

        if (graphId === selectedGraphId) {
            setSelectedGraphId()
        }
    }

    const addDisabledReason = permissionTooltip(canManageDataReview)
        || ((currentWindow?.props?.graphs ?? []).length > 9 ? "There cannot be more than 10 graphs in a Time Series Window." : "")

    const reasonAddMeasurementIsDisabled = (currentGraph?.traces ?? []).length > 4 
        ? "There cannot be more than 5 measurements in a graph."
        : undefined

    const modalBodyGraph = (
        <MobergColumn style={{ padding: "16px" }}>
            <MobergColumn gap="8px">
                <MobergHeader>
                    Graphs
                </MobergHeader>

                <MobergColumn gap="8px">
                    <SortableGraphs />

                    <MobergButton
                        onClick={addGraph}
                        shape={MobergButtonShape.FULL_WIDTH}
                        theme={MobergTheme.BLUE}
                        variant={MobergButtonVariant.OUTLINE}
                        disabled={!!addDisabledReason}
                        tooltip={addDisabledReason}
                    >
                        Add graph
                    </MobergButton>
                </MobergColumn>
            </MobergColumn>
  
        </MobergColumn>
    )

    function saveChanges() {
        setWaitingForNetwork(true)
        modifyLayoutGroup(editedLayoutGroup.id, editedLayoutGroup)
            .then(close)
            .catch(err => {
                setWaitingForNetwork(false)
                setError(err)
            })
        setUneditedLayoutGroup(editedLayoutGroup)
        setTimeSeriesGraphGroup(previous => ({ ...previous, ...currentWindow.props }))
    }

    function closeConfigurationModal() {
        if (madeChanges) {
            createModal(
                <ConfirmationModal
                    title={"Discard Changes?"}
                    description={"You have unsaved changes. Are you sure you want to close without saving?"}
                    confirmButton={{ text: "Close without saving", theme: MobergTheme.RED, onClick: close }}
                />
            )
        } else {
            close()
        }
    }

    function updateGraph(valueOrSetter) {
        setEditedLayoutGroup(previous => {
            const newLayoutGroup = {
                ...previous,
                layouts: previous.layouts.map(layout => {
                    if (layout.id === layoutId) {
                        return {
                            ...layout,
                            areas: layout.areas.map(window => {
                                if (window.area === windowId) {
                                    return {
                                        ...window,
                                        props: {
                                            ...window.props,
                                            graphs: window.props.graphs.map(graph => {
                                                if (graph.id === currentGraph?.id) {
                                                    let newGraph

                                                    if (valueOrSetter instanceof Function) {
                                                        newGraph = valueOrSetter(graph)
                                                    } else {
                                                        newGraph = valueOrSetter
                                                    }

                                                    return { ...newGraph }
                                                }
                                                return graph
                                            })
                                        }
                                    }
                                }
                                return window
                            })
                        }
                    }
                    return layout
                })
            }

            return newLayoutGroup
        })
    }

    function updateGraphProperty(property, valueOrSetter) {
        updateGraph(previousGraph => {
            let newValue

            if (valueOrSetter instanceof Function) {
                newValue = valueOrSetter(previousGraph[property])
            } else {
                newValue = valueOrSetter
            }

            return { ...previousGraph, [property]: newValue }
        })
    }

    function updateDisplayPageSize(newValue) {
        setEditedLayoutGroup(previous => {
            const newLayoutGroup = {
                ...previous,
                layouts: previous.layouts.map(layout => {
                    if (layout.id === layoutId) {
                        return {
                            ...layout,
                            areas: layout.areas.map(window => {
                                if (window.area === windowId) {
                                    return {
                                        ...window,
                                        props: {
                                            ...window.props,
                                            viewDuration: newValue
                                        }
                                    }
                                }
                                return window
                            })
                        }
                    }
                    return layout
                })
            }

            return newLayoutGroup
        })
    }

    function getGraphProperties(graph, renderStrategy = graph.renderStrategy) {
        let graphMin = 0
        let graphMax = 100
        let autoScale = true

        const allTraceDefaults = graph.traces.map(trace => applyModalitySpecificDefaults({...trace}, trace.rawDataKey, renderStrategy))

        if (allTraceDefaults.length === 0) {
            return [graphMin, graphMax, autoScale]
        }
        
        const minimums = allTraceDefaults.map(defaults => defaults.min).filter(value => value !== undefined)
        const maximums = allTraceDefaults.map(defaults => defaults.max).filter(value => value !== undefined)
        const autoscales = allTraceDefaults.map(defaults => defaults.autoscale).filter(value => value !== undefined)

        if (minimums.length > 0) {
            graphMin = Math.min(...minimums)
        }

        if (maximums.length > 0) {
            graphMax = Math.max(...maximums)
        }

        autoScale = autoscales.length === 0 || autoscales.some(value => value)
        
        return [graphMin, graphMax, autoScale]
    }

    function removeTrace(id) {
        updateGraph(previousGraph => {
            const filteredTraces = previousGraph.traces
                .filter(trace => trace.id !== id)
                .filter(trace => trace.renderStrategy === RenderStrategy.DELTA 
                    ? trace.first?.id !== id && trace.second?.id !== id 
                    : true
                )
                
            const [defaultMin, defaultMax, autoScale] = getGraphProperties({ ...previousGraph, traces: filteredTraces })
            
            return {
                ...previousGraph,
                autoScale,
                min: defaultMin,
                max: defaultMax,
                defaultMin,
                defaultMax,
                traces: filteredTraces
            }
        })
    }

    const handleGraphTypeChanged = (renderStrategy) => {
        let defaultProps

        switch (renderStrategy) {
            case RenderStrategy.LINE:
                defaultProps = { color: "#000" }
                break

            case RenderStrategy.HEATMAP:
                defaultProps = {
                    colorSpectrum: ColorSpectrum.INFERNO,
                    lowerBound: 0,
                    upperBound: 100
                }
                break

            case RenderStrategy.SPECTROGRAM:
                defaultProps = {
                    colorSpectrum: ColorSpectrum.DISCRETE_RGB,
                    displayScale: DEFAULT_DSA_DISPLAY_SCALE,
                    lowerBound: 0,
                    upperBound: 2,
                    frequencyLabels: DSA_FREQUENCY_LABELS
                }
                break

            default:
                throw new Error("unexpected graph type: " + renderStrategy)
        }

        updateGraph(previousGraph => {
            const [defaultMin, defaultMax, autoScale] = getGraphProperties(previousGraph, renderStrategy)
            
            return {
                ...previousGraph,
                autoScale,
                min: defaultMin,
                max: defaultMax,
                defaultMin,
                defaultMax,
                renderStrategy,
                traces: previousGraph.traces
                    .filter(trace => trace.renderStrategy !== RenderStrategy.DELTA)
                    .map(trace => (applyModalitySpecificDefaults({ ...trace, ...defaultProps, renderStrategy }, trace.rawDataKey, renderStrategy)))
            }
        })
        
        setValidationErrors(new Map())
    }

    const getModalitiesSection = () => {
        switch (currentGraph?.renderStrategy) {
            case RenderStrategy.HEATMAP:
                return <HeatmapGraphModalities />
            case RenderStrategy.SPECTROGRAM:
                return <SpectrogramGraphModalities />
            case RenderStrategy.LINE:
            default:
                return <LineGraphModalities />
        }
    }

    const validateGraphMinimum = (minValue) => {
        const defaultMax = currentGraph?.defaultMax ?? 100

        if (minValue === -Infinity) {
            return "y-axis minimum should be a real number."
        }

        if (minValue >= defaultMax) {
            return "y-axis minimum value must be less than the graph y-axis max."
        }

        return undefined
    }

    const validateGraphMaximum = (maxValue) => {
        const defaultMin = currentGraph?.defaultMin ?? 0

        if (maxValue <= defaultMin) {
            return "y-axis maximum value must be greater than the graph y-axis min."
        }

        if (maxValue === Infinity) {
            return "y-axis maximum should be a real number."
        }

        return undefined
    }

    const validateGraphHeightRatio = (value) => {
        if (value <= 0) {
            return "Height ratio cannot be less than or equal to 0."
        }

        if (value > 100) {
            return "Height ratio cannot be greater than 100."
        }

        return undefined
    }

    const currentTraces = currentGraph?.traces ?? []

    const activeSourceTraces = currentTraces.filter(trace => 
        trace.renderStrategy !== RenderStrategy.DELTA &&
        (!trace.onDemandAnalysis || isGenericAnalytic(trace.onDemandAnalysis?.analytic))
    )

    const activeAnalyticTraces = currentTraces.filter(trace => trace.onDemandAnalysis?.analytic && !isGenericAnalytic(trace.onDemandAnalysis?.analytic))
    const activeDeltaRegions = currentTraces.filter(trace => trace.renderStrategy === RenderStrategy.DELTA)

    return (
        <ConfigureWindowModalContext.Provider value={{ traceOptions, currentGraph, currentWindow, activeSourceTraces, activeAnalyticTraces, activeDeltaRegions, reasonAddMeasurementIsDisabled, setSelectedGraphId, closeModal: closeConfigurationModal, updateDisplayPageSize, updateGraphProperty, removeTrace, removeGraph, updateGraph, setValidationErrors }}>
            <MobergColumn style={{ width: "90vw", height: "90vh", background: 'var(--color-surface-panel)' }}>

                <ConfigureWindowModalHeader />

                <hr style={{ height: '0px', border: '1px solid var(--color-border-default)', margin: 0 }} />

                <MobergRow style={{ flex: 1, height: "100%", minHeight: 0, overflow: "hidden" }} verticalAlign='start'>
                    <MobergColumn style={{ height: "100%", minWidth: 0, overflow: "auto", background: 'var(--color-surface-panel)' }}>
                        <Scrollbar>
                            {modalBodyGraph}
                        </Scrollbar>
                    </MobergColumn>


                    <MobergColumn style={{ flex: 1, height: "100%", minHeight: 0, minWidth: 0, overflow: "auto" }}>
                        <Scrollbar style={{ flex: 1, display: "flex", borderLeft: '2px solid var(--color-border-default)' }}>
                            <MobergRow style={{ flex: 1, height: "100%" }} expand={false} horizontalAlign={currentGraph ? "start" : "center"}>
                                {!currentGraph
                                    ? (<MobergColumn verticalAlign="center" expand={true}>
                                        <MobergRow horizontalAlign="center" expand={true}>
                                            <MobergText fontSize={MobergFontSize.LARGE} style={{ padding: "16px" }}>
                                                Select a graph to customize
                                            </MobergText>
                                        </MobergRow>
                                    </MobergColumn>
                                    )
                                    : (
                                        <MobergColumn style={{ flex: 1, height: "100%", background: 'var(--color-surface-panel)' }}>
                                            <MobergColumn gap="24px" style={{ padding: "24px", flex: 1 }}>
                                                <MobergColumn gap="16px">
                                                    <MobergHeader>
                                                        Graph properties
                                                    </MobergHeader>

                                                    <MobergRow gap="8px">
                                                        <MobergColumn gap="8px" style={{ padding: "0px 16px" }}>
                                                            <MobergRow gap="8px">
                                                                <MobergInputLabel text="Title" style={{ width: "45px" }} />
                                                                <div>
                                                                    <MobergShortTextInput
                                                                        orientation="row"
                                                                        limit={25}
                                                                        defaultValue={currentGraph?.name}
                                                                        value={currentGraph?.name}
                                                                        onChange={newName => updateGraphProperty("name", newName)}
                                                                    />
                                                                </div>
                                                            </MobergRow>

                                                            <MobergRow gap="8px">
                                                                <MobergInputLabel text="Type" style={{ width: "45px" }} />
                                                                <MobergDropdown
                                                                    selectedValue={currentGraph?.renderStrategy}
                                                                    options={[
                                                                        { label: "Line", value: RenderStrategy.LINE },
                                                                        { label: "Heatmap", value: RenderStrategy.HEATMAP },
                                                                        { label: "Spectrogram", value: RenderStrategy.SPECTROGRAM }
                                                                    ]}
                                                                    onChange={handleGraphTypeChanged}
                                                                />
                                                            </MobergRow>

                                                        <MobergRow gap="8px">
                                                            <MobergInputLabel text="Height ratio" />
                                                            <MobergNumberInput
                                                                key={`height-ratio-${currentGraph?.id}`}
                                                                defaultValue={currentGraph?.heightRatio ?? 1}
                                                                value={currentGraph?.heightRatio}
                                                                onChange={newValue => updateGraphProperty("heightRatio", newValue)}
                                                                validationFunction={validateGraphHeightRatio}
                                                            />
                                                        </MobergRow>

                                                        </MobergColumn>

                                                        {currentGraph?.renderStrategy !== RenderStrategy.HEATMAP && currentGraph?.renderStrategy !== RenderStrategy.SPECTROGRAM && (<>
                                                            <MobergColumn gap="8px" style={{ padding: "0px 16px" }}>
                                                                <MobergRow gap="8px">
                                                                    <MobergInputLabel text="Y axis min" style={{ width: "80px" }} />
                                                                    <MobergNumberInput
                                                                        key={`y-axis-min-${currentGraph?.id}`}
                                                                        defaultValue={currentGraph?.defaultMin ?? 0}
                                                                        value={currentGraph?.defaultMin}
                                                                        onChange={newValue => {
                                                                            updateGraphProperty("defaultMin", newValue)
                                                                            updateGraphProperty("min", newValue)
                                                                        }}
                                                                        validationFunction={validateGraphMinimum}
                                                                    />
                                                                </MobergRow>

                                                                <MobergRow gap="8px">
                                                                    <MobergInputLabel text="Y axis max" style={{ width: "80px" }} />
                                                                    <MobergNumberInput
                                                                        key={`y-axis-max-${currentGraph?.id}`}
                                                                        defaultValue={currentGraph?.defaultMax ?? 100}
                                                                        value={currentGraph?.defaultMax}
                                                                        onChange={newValue => {
                                                                            updateGraphProperty("defaultMax", newValue)
                                                                            updateGraphProperty("max", newValue)
                                                                        }}
                                                                        validationFunction={validateGraphMaximum}
                                                                    />
                                                                </MobergRow>
                                                                <MobergRow gap="8px">
                                                                    <MobergInputLabel text={"Auto-scale"} />

                                                                    <MobergButton
                                                                        shape={MobergButtonShape.SQUARE}
                                                                        theme={currentGraph?.autoScale ? MobergTheme.BLUE : MobergTheme.BLACK}
                                                                        onClick={() => updateGraphProperty("autoScale", !currentGraph?.autoScale)}
                                                                    >
                                                                        {currentGraph?.autoScale
                                                                            ? <MdCheckBox size={MobergIconSize.REGULAR} />
                                                                            : <MdCheckBoxOutlineBlank size={MobergIconSize.REGULAR} />
                                                                        }

                                                                    </MobergButton>
                                                                </MobergRow>

                                                            </MobergColumn>
                                                        </>
                                                        )}
                                                    </MobergRow>
                                                </MobergColumn>

                                                <hr style={{ border: "none", borderTop: "1px solid var(--color-border-default)", margin: 0 }} />

                                                {getModalitiesSection()}

                                                <hr style={{ border: "none", borderTop: "1px solid var(--color-border-default)", margin: 0 }} />

                                                {currentGraph?.renderStrategy !== RenderStrategy.SPECTROGRAM && (
                                                    <MultiGraphNumericDisplayConfiguration />
                                                )}

                                            </MobergColumn>
                                        </MobergColumn>
                                    )
                                }
                            </MobergRow>
                        </Scrollbar>
                    </MobergColumn>
                </MobergRow>

                <hr style={{ height: '0px', border: '1px solid var(--color-border-default)', margin: 0 }} />

                <ModalFooter>
                    <MobergRow gap="16px" horizontalAlign="right">
                        {waitingForNetwork && (
                            <div style={{ display: "flex", alignItems: "center" }}> Please wait... </div>
                        )}

                        {!waitingForNetwork && error && (
                            <div style={{ display: "flex", alignItems: "center", color: red }}> {error} </div>
                        )}

                        {validationErrorsArray.length > 0 && (
                            <MobergText style={{ color: red }}>
                                {validationErrorsArray[0]}
                            </MobergText>
                        )}

                        <MobergButton
                            disabled={!madeChanges || waitingForNetwork || Array.from(validationErrors).length > 0 || !canManageDataReview}
                            tooltip={permissionTooltip(canManageDataReview)}
                            onClick={saveChanges}
                            theme={MobergTheme.BLUE}
                            variant={MobergButtonVariant.FILLED}
                            fontSize={MobergFontSize.LARGE}
                            shape={MobergButtonShape.WIDE}
                        >
                            Save
                        </MobergButton>
                    </MobergRow>
                </ModalFooter>
            </MobergColumn>
        </ConfigureWindowModalContext.Provider>
    )
}

export default ConfigureWindowModal;
